import mixpanel from 'mixpanel-browser';
import { environment } from 'src/environments/environment';
import { getStoredAdParams, AD_PARAM_NAMES } from './gtm.util';

/** Cap a single ad-param value so an oversized value can't bloat the shared cross-subdomain cookie. */
const MAX_AD_VALUE_LEN = 200;

/**
 * Marks that this device's first touch has been recorded as one atomic set.
 * Lives in the shared .realworld.fi cookie so the landing surface and this app agree:
 * whichever fires first wins and the other skips.
 * Keep the literal in sync with landing-page/assets/js/mixpanel-init.js.
 */
const FIRST_TOUCH_SENTINEL = 'first_touch_captured';

/** first_* super-prop names, derived from the shared ad-param allowlist. */
const FIRST_TOUCH_KEYS: string[] = AD_PARAM_NAMES.map((name) => `first_${name}`);

/**
 * Has a first touch already been recorded on this device?
 *
 * True when the sentinel is set, OR when ANY first_* prop already exists. That second
 * clause is deliberate: users attributed before the sentinel existed carry a
 * (possibly partial) first_* set with no sentinel, and rewriting it would destroy their
 * genuine first touch. Existing data counts as an implicit sentinel and is left alone —
 * null-forward only, no retro-rewrite.
 */
function hasFirstTouch(): boolean {
  try {
    if (mixpanel.get_property(FIRST_TOUCH_SENTINEL)) return true;
    return FIRST_TOUCH_KEYS.some((key) => Boolean(mixpanel.get_property(key)));
  } catch {
    // Persistence unreadable — assume captured, so an unknown state is never clobbered.
    return true;
  }
}

/**
 * The first_* super-props currently registered on this device, as one coherent set.
 * The People-profile backstop uses this so the profile records the SAME visit the
 * super-props recorded, rather than rebuilding an independent set from the ad store.
 */
export function getRegisteredFirstTouch(): Record<string, string> {
  const out: Record<string, string> = {};
  try {
    for (const key of FIRST_TOUCH_KEYS) {
      const value = mixpanel.get_property(key);
      if (typeof value === 'string' && value) out[key] = value;
    }
  } catch {
    // no-op: analytics must not break user flows.
  }
  return out;
}

const isMixpanelEnabled = () => Boolean(environment.ENABLE_MIXPANEL && environment.MIXPANEL_TOKEN);

// Module-level dedupe state. Auth flow fires 5-6 state mutations per sign-in; each
// was previously triggering identify+register+people.set with the same payload.
// Collapse consecutive identical identify calls to one. Reset on mixpanelReset().
let lastIdentifyKey: string | null = null;

// The path+query we last emitted a pageview for. Reset on mixpanelReset().
//
// NavigationEnd is NOT the same thing as "the URL changed". Angular emits it for
// internal detours too — redirectNotifications() in header-new.component.ts does
// `navigateByUrl('/', { skipLocationChange: true })` before navigating to the real
// target, and skipLocationChange suppresses the history update while still completing
// the navigation. On that intermediate event window.location still points at the page
// the user is LEAVING, so firing unconditionally emits a spurious duplicate pageview
// attributed to the wrong page.
//
// Keying on the browser URL rather than comparing router URL to window.location is
// deliberate: the router serialises its own URL, so the two can differ by percent-
// encoding on routes carrying addresses or ids (/nft-detail/0xABC…/123) and a false
// mismatch would SILENTLY DROP real pageviews — the same class of data loss this whole
// change exists to fix. Comparing window.location to itself cannot drift that way.
//
// This restores exactly the semantics of the autocapture `locationchange` listener we
// replaced: one pageview per actual URL change, query included.
let lastPageviewUrl: string | null = null;

/**
 * Initialise Mixpanel and immediately stamp the `environment` super-property so that
 * every track() call — including pre-auth events — carries the correct env label.
 * Call once at app bootstrap (main.ts) in place of the bare mixpanel.init().
 */
export function mixpanelInit(): void {
  if (!isMixpanelEnabled()) return;
  try {
    mixpanel.init(environment.MIXPANEL_TOKEN, {
      debug: !(environment as any).production,
      // MUST stay false. With track_pageview:true the SDK fires the pageview INSIDE
      // init(), i.e. before the register() calls below have run — so the first
      // $mp_web_page_view of every cold session shipped with no `environment` and no
      // first_*/last-touch props. Any report filtered on environment == 'PRODUCTION'
      // silently dropped the first pageview of every session, and the ad-click landing
      // pageview carried no attribution at all. Pageviews are now fired exclusively
      // from app.component's NavigationEnd handler via trackPageview(), which cannot
      // run until bootstrap has finished registering the super-props. Exactly one per
      // route. (Dead in practice either way — see the autocapture note below: passing
      // an autocapture OBJECT means this top-level key is never the one consulted.)
      track_pageview: false,
      // Cross-subdomain identity is LOAD-BEARING: the same Mixpanel identity must
      // span realworld.fi (landing) and app.realworld.fi (this app). We set these
      // explicitly rather than relying on mixpanel-browser defaults so the intent
      // survives any future SDK default change. The cookie name (mp_<token>_mixpanel)
      // is the JOIN KEY across surfaces — do NOT set a custom persistence_name.
      // Flipping from the previous 'localStorage' migrates cleanly: mixpanel-
      // persistence.js:112-119's upgrade() copies the ENTIRE persisted store (all
      // super-props, not just distinct_id/$device_id/$user_id) into the cookie via
      // register_once on the first post-deploy load — identity is preserved (no
      // reset) for existing users, but legacy super-props like wallet_address ride
      // along too. That's why mixpanelInit() unregisters wallet_address right
      // after init() below.
      // NOTE: unconditional for all envs. dev/.env.staging share ONE token, so
      // devmarket + stagingmarket will share a single .realworld.fi identity store.
      // That dev/staging bleed is ACCEPTED (scope is prod); staging also rehearses
      // the prod cookie-persistence path as a side benefit.
      persistence: 'cookie',
      cross_subdomain_cookie: true,
      secure_cookie: true,
      // `pageview: false` is LOAD-BEARING, not tidying. The SDK resolves pageview
      // tracking as: if the `autocapture` config is truthy, read `autocapture.pageview`;
      // otherwise read the top-level `track_pageview`. Because we pass an autocapture
      // OBJECT, the top-level `track_pageview: false` above is in the branch that never
      // runs — it has been doing nothing. `autocapture.pageview` was falling through to
      // its default of 'full-url', so autocapture fired its own pageview on every URL
      // change, on top of the one this function used to hand-fire at bootstrap — that
      // pairing is what double-counted every pageview in production.
      //
      // ⚠️ Autocapture was not only duplicating — it installed a `locationchange`
      // listener and fired a pageview on EVERY URL change, which is where all our
      // in-app route pageviews came from. Turning it off therefore makes route tracking
      // OUR responsibility: trackPageview() is fired from app.component on
      // NavigationEnd. Remove that and in-app navigation stops being counted entirely —
      // one pageview per session instead of one per page.
      autocapture: { allow_selectors: ['[data-mp-track]'], pageview: false },
      record_sessions_percent: 10,
      // SECURITY — never send a query string to a third party.
      // The SDK's `_.info.properties()` stamps `$current_url` (window.location.href,
      // query and all) onto EVERY event, and `track_pageview` adds `current_url_search`
      // on top. The password-reset route and the email-verification link both carry
      // their token in the query string, and verify-email carries the address itself —
      // all of which would be handed to Mixpanel verbatim and written to their logs.
      //
      // property_blacklist rather than sanitising at each call site: the SDK applies it
      // after merging persistence, super-props, session-recording and event properties,
      // so it is the last word. Nothing a future track() call, an autocapture event or
      // an SDK upgrade does can reintroduce these.
      //
      // Losing `$current_url` outright WOULD cost real page context, because it is the
      // only URL property non-pageview events ever carry (`current_url_path` and
      // friends are added by track_pageview alone). So `page_url` below replaces it
      // with the same value minus the query — registered as a super-prop, it rides
      // every event exactly as `$current_url` did.
      property_blacklist: ['$current_url', 'current_url_search'],
    });
    // Purge any legacy wallet_address super-prop. upgrade() (mixpanel-persistence.js:
    // 112-119) migrates the ENTIRE old localStorage store into the shared cookie via
    // register_once on the first post-deploy load — so every already-signed-in user's
    // persisted wallet_address would otherwise land in the .realworld.fi cookie (-> API
    // logs). Dropping register() in mixpanelIdentify only stops NEW writes; unregister
    // here deletes the migrated key (and saves) so it never persists for existing users.
    // Each post-init step is guarded independently so that one failing registration
    // cannot take the others down with it — that coupling is exactly what a single
    // wrapping try/catch would reintroduce.
    try {
      mixpanel.unregister('wallet_address');
    } catch {
      // no-op: analytics must not break user flows.
    }
    try {
      // environment stays a super-prop (single-surface-per-token — no cross-surface
      // clobber). Do NOT register `source` here — see mixpanelTrack (Decision 2).
      // Stamp environment immediately so pre-auth events are not under-reported.
      mixpanel.register({ environment: environment.ENVNAME });
    } catch {
      // no-op: analytics must not break user flows.
    }

    // Query-free stand-in for the blacklisted $current_url. Must be re-registered on
    // every route change — app.component does that on NavigationEnd — or every event
    // after the first would report the landing URL.
    registerPageUrl();

    // Acquisition super-props at bootstrap, so they are registered before any event —
    // including the first NavigationEnd pageview — can leave. captureAdParams() has
    // already run (main.ts, ahead of this call), so the sessionStorage store is
    // populated by now. Internally guarded — never throws.
    registerAcquisitionProps();

    // NOTE: no pageview is fired here. app.component fires one on every Angular
    // NavigationEnd, and Angular emits NavigationEnd for the initial navigation too —
    // so bootstrapping one here as well would double-count the first page all over
    // again, and would report the pre-redirect URL ("/" rather than "/dashboard").
    // See trackPageview() below.
  } catch {
    // no-op: analytics must not break user flows. Only mixpanel.init() itself can
    // reach here, and without a live SDK there is nothing left worth attempting.
  }
}

/**
 * Register `page_url` — the current URL with the query string removed.
 *
 * `$current_url` is blacklisted because it carries the raw query, and on the
 * password-reset and email-verification routes that query contains a live token. But
 * `$current_url` is also the ONLY URL property that non-pageview events carry, so
 * dropping it without a replacement would leave every Sign Up, Loan Accepted and
 * autocapture event with no page context at all.
 *
 * Registered as a super-prop so it rides every event, exactly as `$current_url` did —
 * same value, minus the part that leaks. Call on every route change.
 */
export function registerPageUrl(): void {
  if (!isMixpanelEnabled()) return;
  try {
    if (typeof window === 'undefined') return;
    // origin + pathname only. Hash is dropped too — Angular does not use it for
    // routing here, and it is another place a token could be parked.
    const { origin, pathname } = window.location;
    // Never persist a blank or opaque origin: this lands in the SHARED .realworld.fi
    // cookie, so a bad value would blank page attribution on the landing too and stay
    // there until something overwrote it. No value beats a wrong value.
    if (!origin || origin === 'null') return;
    mixpanel.register({ page_url: `${origin}${pathname}` });
  } catch {
    // no-op: analytics must not break user flows.
  }
}

/**
 * Fire one pageview for the route we are now on.
 *
 * Called from app.component on every NavigationEnd, INCLUDING the initial navigation,
 * so this is the only place pageviews come from.
 *
 * Why not autocapture's: it installs its own locationchange listener and fires a
 * pageview on every URL change, which is where our duplicate came from. Its version
 * also fires without any guarantee that `environment` and the acquisition super-props
 * have been registered — the exact ordering problem 86e2gpk27 fixed. Ours runs after
 * registerPageUrl() in the same handler, so every pageview carries the full set.
 *
 * Why not at bootstrap: Angular emits NavigationEnd for the first navigation as well,
 * so firing in mixpanelInit() too would re-create the double-count, and would report
 * the pre-redirect URL rather than the page the user actually lands on.
 *
 * Emits at most one pageview per distinct path+query — see lastPageviewUrl. A
 * NavigationEnd that leaves the browser URL untouched (skipLocationChange) is a detour,
 * not a page, and is skipped.
 */
export function trackPageview(): void {
  if (!isMixpanelEnabled()) return;
  try {
    if (typeof window === 'undefined') return;
    // Query INCLUDED: filter/pagination changes are genuine pageviews, and autocapture's
    // default was 'full-url', so dropping them here would lose events prod counts today.
    // Never sent anywhere — this is a local comparison key only, which is why it can
    // carry the query that `page_url` deliberately strips.
    const currentUrl = `${window.location.pathname}${window.location.search}`;
    if (currentUrl === lastPageviewUrl) return;
    lastPageviewUrl = currentUrl;
    mixpanel.track_pageview();
  } catch {
    // no-op: analytics must not break user flows.
  }
}

export function mixpanelIdentify(distinctId?: string, walletAddress?: string) {
  if (!isMixpanelEnabled() || !distinctId) return;

  // Cheap stable key — distinct_id first, wallet second (order matters for equality).
  const key = `${distinctId}::${walletAddress ?? ''}`;
  if (key === lastIdentifyKey) return;
  lastIdentifyKey = key;

  try {
    mixpanel.identify(distinctId);

    // wallet_address is kept ONLY on the People profile (people.set below) — NOT
    // registered as a super-property. A registered super-prop lands in the shared
    // .realworld.fi cookie, which would ride to *.api.realworld.fi (wallet in API
    // logs) and bleed onto other subdomains' events. People props are server-side
    // per distinct_id, so segmenting by wallet still works without that exposure.

    // Persist wallet_address on the People profile.
    const peopleProps: Record<string, unknown> = { environment: environment.ENVNAME };
    if (walletAddress) peopleProps['wallet_address'] = walletAddress;
    mixpanel.people?.set?.(peopleProps);
  } catch {
    // no-op: analytics must not break user flows.
    // On failure, clear the dedupe key so a retry is possible next call.
    lastIdentifyKey = null;
  }
}

export function mixpanelPeopleSet(properties: Record<string, any>) {
  if (!isMixpanelEnabled() || !properties) return;
  try {
    mixpanel.people?.set?.(properties);
  } catch {
    // no-op: analytics must not break user flows
  }
}

export function mixpanelPeopleSetOnce(properties: Record<string, any>) {
  if (!isMixpanelEnabled() || !properties) return;
  try {
    mixpanel.people?.set_once?.(properties);
  } catch {
    // no-op: analytics must not break user flows
  }
}

/**
 * Register acquisition super-properties from the sessionStorage ad-param store.
 * Called from mixpanelInit() (which main.ts invokes AFTER captureAdParams(), so the
 * store is populated) and safe to call again — first touch is idempotent by design.
 *
 * FIRST TOUCH IS ATOMIC PER VISIT. `mixpanel.register_once()` is write-once *per
 * property*, not per call, so the previous implementation let a later, unrelated visit
 * fill in whichever first_* keys the original visit happened to lack. Observed on prod:
 * first_gclid/first_utm_source from one visit sitting beside a first_utm_campaign from
 * another 11h later. That is the common case, not an edge case — Google auto-tagging
 * delivers `gclid` with no `utm_campaign` at all. So we gate the whole first-touch set
 * behind ONE sentinel and write it in a single register() call.
 *
 * Unprefixed keys use register: overwrites ONLY when the session carries params;
 * empty store → skipped → prior last-touch persists (correct attribution).
 * gclid/gbraid/wbraid are not auto-captured by Mixpanel, so they are registered here.
 * Empty store → no register call (no phantom keys).
 */
export function registerAcquisitionProps(): void {
  if (!isMixpanelEnabled()) return;

  // Enforce whitelist-only at the register site (defense-in-depth): copy ONLY the known
  // AD_PARAM_NAMES keys, string values, trimmed + length-capped. This makes the
  // "whitelist-only" invariant true regardless of what's in sessionStorage, and stops a
  // tampered/oversized value from polluting or bloating the shared cross-subdomain cookie.
  // Non-string values and any other key (incl. __proto__/constructor) are ignored.
  const ad = getStoredAdParams();
  if (!ad || typeof ad !== 'object') return; // defensive: never index a null/non-object
  const lastTouch: Record<string, string> = {};
  for (const key of AD_PARAM_NAMES) {
    // Own-property read for the same reason as the unregister loop below: a polluted
    // Object.prototype would otherwise supply a value this store never contained, which
    // is exactly what the "any other key is ignored" claim above promises it cannot.
    if (!Object.prototype.hasOwnProperty.call(ad, key)) continue;
    const v = ad[key];
    if (typeof v === 'string') {
      const trimmed = v.trim().slice(0, MAX_AD_VALUE_LEN);
      if (trimmed) lastTouch[key] = trimmed;
    }
  }

  if (Object.keys(lastTouch).length === 0) return; // nothing captured → no-op

  const firstTouch: Record<string, string> = {};
  for (const [k, v] of Object.entries(lastTouch)) {
    firstTouch[`first_${k}`] = v;
  }

  // Guard each call independently — a first-touch failure must NOT drop last-touch
  // registration (best-effort analytics; analytics must never break user flows).
  try {
    if (!hasFirstTouch()) {
      // Whole set in ONE register(), plus the sentinel, so this visit's first touch can
      // never be completed later by a different visit's params.
      mixpanel.register({ ...firstTouch, [FIRST_TOUCH_SENTINEL]: true });
    }
  } catch {
    // no-op
  }
  // A fresh ad click REPLACES the whole last-touch set. Without this unregister pass
  // register() merges: a ?utm_source=facebook click keeps the PREVIOUS campaign's gclid,
  // and every event then carries utm_source=facebook + gclid=<old google id>. That is the
  // same hybrid attribution captureAdParams already refuses to fabricate at the storage
  // layer — the super-props layer has to match it, or the two disagree about what last
  // touch means.
  //
  // hasOwnProperty, not `in`: `in` walks the prototype chain, so a polluted
  // Object.prototype carrying an ad-param key would skip the unregister and preserve the
  // stale value — reintroducing the hybrid last touch this loop exists to prevent.
  //
  // Guarded PER KEY, and separately from register() below. Sharing one try would mean a
  // single unregister throw both stops the remaining keys being cleared AND skips the
  // last-touch write entirely — losing this visit's attribution to salvage nothing.
  for (const key of AD_PARAM_NAMES) {
    if (Object.prototype.hasOwnProperty.call(lastTouch, key)) continue;
    try {
      mixpanel.unregister(key);
    } catch {
      // no-op: one uncleared key must not stop the rest, nor the register below.
    }
  }
  try {
    // Last touch: overwrites ONLY when the session carries params;
    // empty store → skipped above → prior last-touch persists (correct attribution).
    mixpanel.register(lastTouch);
  } catch {
    // no-op
  }
}

export function mixpanelTrack(eventName: string, properties: Record<string, any> = {}) {
  if (!isMixpanelEnabled() || !eventName) return;
  try {
    // `source` MUST be a per-event property, never a registered super-prop.
    // register() writes to the SHARED cross-subdomain cookie (mixpanel-persistence.js:184-195),
    // and track() merges those super-props into every event at track time
    // (mixpanel-core.js:1204-1212). A registered `source` is last-writer-wins across
    // surfaces: the app tab overwrites the shared cookie, the landing tab's next event
    // then reads source:'app' — mislabeling the exact landing→app movers this funnel
    // measures. Passing it per-event here is clobber-proof. Caller value wins if set.
    mixpanel.track(eventName, { source: 'app', ...properties });
  } catch {
    // no-op: analytics must not break user flows
  }
}

export function mixpanelReset(): void {
  // Always clear the dedupe keys even if Mixpanel is disabled —
  // otherwise a later enable+identify could be skipped erroneously.
  lastIdentifyKey = null;
  // Same reasoning for the pageview key: after a logout the next user starts a fresh
  // session, and landing back on the page they logged out from must still count.
  lastPageviewUrl = null;
  if (!isMixpanelEnabled()) return;
  try {
    mixpanel.reset();
  } catch {
    // no-op: analytics must not break user flows
  }
}
